import React from 'react';
import { Link } from '@inertiajs/react';
import { Card } from '~/marketing/ui';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { LegalPage, Section, H3, P, UL } from '~/marketing/LegalPage';
import { legal } from '~/marketing/legalData';
import { routes } from '~/marketing/routes';
import { quoted } from '~/marketing/marketingData';

/* Adapted from `landing/privacy.html`. Four corrections, and the last one is
   the one that mattered:

   **"Visibility scores"** — this product refuses to compute a score. It reports
   a count over a denominator with the exclusions listed beside it, which is the
   whole argument for trusting it. A privacy policy is a strange place to
   reintroduce the one number the product will not invent.

   **"Weekly digests" and "alerts"** appeared twice as things we send. Neither
   exists. What the Service actually sends is listed instead, because a privacy
   policy naming messages nobody receives is inaccurate in the direction that
   looks like over-collection.

   **Analytics and error monitoring were named as processors**, and there are
   none — no analytics package and no error tracker is installed. Claiming a
   processor that does not exist is a false statement about who receives your
   data, and it cost nothing to remove because the honest position is better:
   the only cookies are the ones that keep you signed in.

   **Deletion was described as a 90-day restore window.** It is not: deletion
   cascades immediately and cannot be undone. The account page states in Class 2
   copy exactly what deletion removes and the two things it keeps, and this page
   now says the same thing — two pages describing one behaviour two ways is the
   drift the copy rules exist to stop. */

export default function Privacy() {
  return (
    <LegalPage
      title="Privacy Policy"
      path={routes.privacy}
      updated={legal.privacyUpdated}
      description="What Spruce collects, why, and what control you have over it. The only cookies are the ones that keep you signed in."
      lede={`What ${legal.business} collects when you use ${legal.product}, why, and what control you have over it.`}
    >
      <Card variant="signal" pad="lg">
        <P>
          <strong>We never see your card details.</strong> Payments are handled by {legal.mor} as
          merchant of record. Card numbers are entered on their systems and are never transmitted to
          or stored by us.
        </P>
      </Card>

      <Section title="1. What we collect">
        <H3>Information you give us</H3>
        <UL>
          <li><strong>Account details</strong> — your email address, and your name if you choose to add one.</li>
          <li><strong>Business details</strong> — the businesses you ask us to measure: their names and any other names you add, websites, cities and industries.</li>
          <li><strong>Free checks</strong> — if you run a free check: your email address, and the name, city, state and industry of the business you ask us to check.</li>
          <li><strong>Correspondence</strong> — messages you send to support.</li>
        </UL>

        <H3>Information we generate</H3>
        <UL>
          <li><strong>Collected answers</strong> — the responses third-party AI systems return to your questions, and the citations within them.</li>
          <li><strong>Derived measurements</strong> — which businesses were named, which sources were cited, and how those counts change over time.</li>
        </UL>

        <H3>Information collected automatically</H3>
        <UL>
          <li><strong>Usage and technical data</strong> — pages viewed, timestamps, IP address, browser and device type, referring page, kept in server logs.</li>
        </UL>

        <P>
          We do not knowingly collect information from anyone under 18, and we do not collect
          special categories of personal data.
        </P>
      </Section>

      <Section title="2. Why we use it">
        <UL>
          <li>To provide the Service — running collection and displaying your results.</li>
          <li>To operate your account, including signing you in and tracking subscription status.</li>
          <li>To send account messages: email verification, password resets, confirming a change of email address, telling you when your first measurement is ready, and notices about your trial or subscription.</li>
          <li>To run a free check: emailing you its report, and limiting each business to one free check every 60 days.</li>
          <li>To provide support when you contact us.</li>
          <li>To secure the Service, prevent abuse, and enforce plan limits.</li>
          <li>To improve the Service, including analyzing aggregate usage.</li>
          {/*
            Approved by the founder 2026-10-12, so that grader results can become
            marketing figures — aggregated, never naming a business outside the
            opt-in share flow, each figure carrying its limits. Before this
            sentence the page allowed *analysing* aggregate usage and *keeping*
            de-identified statistics, and neither is publishing. The rule we
            hold ourselves to on top of it: only checks run on or after this
            policy's effective date go into a published figure.
          */}
          <li>To publish aggregated findings from free checks — for example, how often businesses in one industry are named by AI assistants. A published figure never identifies you or any business you asked us to check.</li>
        </UL>
        <P>
          Where the GDPR applies, our legal bases are performance of a contract, legitimate
          interests (security, service improvement, and communicating with existing customers),
          consent where we ask for it, and legal obligation.
        </P>
      </Section>

      <Section title="3. Who we share it with">
        <P>
          We do not sell your personal information and we do not share it with third parties for
          their own marketing. We use these categories of processor:
        </P>
        <UL>
          <li><strong>Payments</strong> — {legal.mor}, as merchant of record, for billing, tax and invoicing.</li>
          <li><strong>Hosting and database</strong> — our cloud infrastructure and managed database provider.</li>
          <li>
            <strong>AI and search providers</strong> — the AI platforms and search APIs we query on
            your behalf. We send them the questions, the answers we ask a model to read, and any
            industry you describe in your own words. We do not send them your name, your email
            address or any billing detail.
          </li>
          <li><strong>Email delivery</strong> — our transactional email provider, for the account messages listed above.</li>
        </UL>
        <P>
          We may also disclose information where required by law, to protect our rights or the
          safety of others, or in connection with a merger or acquisition — in which case we will
          tell you before your information becomes subject to a different policy.
        </P>
        <P>A current list of subprocessors is available on request from {legal.supportEmail}.</P>
      </Section>

      <Section title="4. International transfers">
        <P>
          We are based in the United States and our providers may process data in the United States
          and elsewhere. Where we transfer personal data out of the UK, EEA or Switzerland we rely
          on appropriate safeguards such as Standard Contractual Clauses.
        </P>
      </Section>

      <Section title="5. How long we keep it, and what deleting removes">
        <P>
          <strong>Canceling and deleting are different events.</strong> Canceling stops
          collection and removes nothing at the time, so your history is still here if you come
          back. Deleting your account removes your businesses, your questions and every answer
          collected for them, immediately.
        </P>
        {/*
          Both strings are read rather than restated, 2026-10-09 with item 17.
          This paragraph already held `retention` word for word, so a reader sees
          no change here at all — only the source moved. The account sentence
          beside it stays Class 1 and is worded identically on `/refund`, because
          one rule stated two ways is how a reader concludes there is no rule.
        */}
        <P>{quoted.retention} Your account is kept until you delete it.</P>
        {/*
          The deletion paragraph held the sanctioned string's first sentence
          verbatim and **paraphrased its last two into list items** — "A global
          list of which websites get cited in AI answers. It contains no personal
          data and no link to your account." against the sanctioned "We keep a
          global list … ; it contains …". A Class 2 paraphrase on the page a
          reader is sent to for the policy. The list is gone with it: the string
          says "Two things are kept" and then names them, so rendering it as a
          list meant rewriting the two things to fit the bullets.
        */}
        <P>{quoted.deletion}</P>
        <P>Deleting takes effect immediately and cannot be undone.</P>
        <P>How long each kind of information is kept:</P>
        <UL>
          <li><strong>Collected answers and measurements</strong> — kept while a plan runs and for twelve months after it ends. You may remove a single business and its history at any time.</li>
          <li><strong>Billing records</strong> — kept by {legal.mor} and by us for as long as tax and accounting law requires, typically seven years.</li>
          <li><strong>Support correspondence</strong> — up to two years.</li>
          <li><strong>Server logs</strong> — a short operational window, then discarded.</li>
        </UL>
        {/*
          Outside the list because a free check belongs to no account. The window
          is swept by `Retention::PurgeFreeChecks` every day, and
          `purge_free_checks_test` fails if this sentence and the sweep stop
          naming the same number — the rule the paid-plan window follows.
        */}
        <P>
          <strong>Free checks</strong> — kept for twelve months after the business was last checked,
          then deleted.
        </P>
        <P>
          Aggregated, de-identified statistics that cannot reasonably be linked back to you may be
          kept indefinitely.
        </P>
      </Section>

      <Section title="6. Your rights">
        <P>
          Depending on where you live you may have the right to access, correct, delete or receive a
          copy of your personal data; to object to or restrict certain processing; and to withdraw
          consent. California residents have the right to know what is collected, to delete it, to
          correct it, and not to be treated differently for exercising those rights — and we confirm
          we do not sell or share personal information as the CCPA and CPRA define those terms.
        </P>
        <P>
          To exercise any right, email <a href={`mailto:${legal.supportEmail}`}>{legal.supportEmail}</a>{' '}
          and we will respond within 30 days. If you are in the UK or the EEA, you can also submit a
          complaint or concern to the data protection authority where you live; in the UK, that is
          the Information Commissioner&rsquo;s Office.
        </P>
        {/* Reworded 2026-10-26 on the founder's review. It read "If you are in the
            UK or EEA and are unhappy with our response, you may complain to your
            local supervisory authority": the right is kept because UK and EU GDPR
            require a notice to state it, but it never depended on our response.
            "Concern" is the Information Commissioner's Office's own word. */}
      </Section>

      <Section title="7. Security">
        <P>
          We use encryption in transit, encryption at rest for stored data, access controls, and
          least-privilege access to production systems. Sessions are stored server-side, so signing
          out or resetting your password revokes access rather than merely expiring a cookie. No
          system is perfectly secure; if a breach affects your personal data we will notify you and
          any required regulator without undue delay.
        </P>
      </Section>

      <Section title="8. Cookies">
        <P>
          <strong>We set only the cookies needed to run the Service</strong> — keeping you signed in
          and maintaining your session. We do not use analytics, advertising or cross-site tracking
          cookies, we run no third-party ad pixels, and the site loads no fonts or scripts from a
          third-party CDN.
        </P>
      </Section>

      <Section title="9. Changes to this policy">
        <P>
          We may update this policy. If a change is material we will tell you by email or in the
          Service before it takes effect. The &ldquo;last updated&rdquo; date above always reflects
          the current version.
        </P>
      </Section>

      <Section title="10. Related">
        <P>
          <Link href={routes.terms}>Terms of Service</Link> ·{' '}
          <Link href={routes.refund}>Refund Policy</Link> ·{' '}
          <Link href={routes.contact}>Contact</Link>
        </P>
      </Section>
    </LegalPage>
  );
}

Privacy.layout = withMarketingLayout;
